import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Table,
  Modal,
  TextInput,
  Alert,
  Spinner,
  Pagination,
  Select,
} from 'flowbite-react';
import { fetchWithAuth } from '../utils/api';
import { getPagesEndpoints } from '../config/api';
import { getValidationMessages, formatDate } from '../utils/helper';
// @ts-ignore
import FacebookLogin from 'react-facebook-login/dist/facebook-login-render-props';

interface PageItem {
  id: number;
  name: string;
  page_id?: string | null;
  source_type: string;
  status: string;
  created_at: string;
  updated_at: string;
}

interface PageFormData {
  name: string;
  page_id: string;
  source_type: string;
}

const Pages: React.FC = () => {
  const [pages, setPages] = useState<PageItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<{
    type: 'success' | 'failure';
    text: string;
  } | null>(null);
  const [messagePopup, setMessagePopup] = useState<{
    type: 'success' | 'failure';
    text: string;
  } | null>(null);

  // Search
  const [searchTerm, setSearchTerm] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 10;

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [editingPage, setEditingPage] = useState<PageItem | null>(null);

  // Form
  const [formData, setFormData] = useState<PageFormData>({
    name: '',
    page_id: '',
    source_type: 'facebook',
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  // Load list
  const loadPages = async (page = 1, search = '') => {
    try {
      setLoading(true);
      setError(null);

      const pagesEndpoint = getPagesEndpoints().LIST;
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: itemsPerPage.toString(),
        ...(search && { search: search }),
      });

      const response = await fetchWithAuth(`${pagesEndpoint}?${params}`);

      if (response.success && response.data) {
        setPages(response.data.results || []);
        setTotalPages(Math.ceil((response.data.count || 0) / itemsPerPage));
        setTotalItems(response.data.count || 0);
      } else {
        setError('Không thể tải danh sách page');
      }
    } catch (err) {
      setError('Đã xảy ra lỗi khi tải dữ liệu');
      console.error('Load pages error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPages(1, searchTerm);
  }, []);

  // Search
  const handleSearch = () => {
    setCurrentPage(1);
    loadPages(1, searchTerm);
  };

  // Pagination
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    loadPages(page, searchTerm);
  };

  // Open create
  const openCreateModal = () => {
    setModalMode('create');
    setEditingPage(null);
    setFormData({ name: '', page_id: '', source_type: 'facebook' });
    setFormErrors({});
    setShowModal(true);
    setMessagePopup(null);
  };

  // Open edit
  const openEditModal = (pageItem: PageItem) => {
    setModalMode('edit');
    setEditingPage(pageItem);
    setFormData({
      name: pageItem.name,
      page_id:
        pageItem.page_id === null || pageItem.page_id === undefined
          ? ''
          : String(pageItem.page_id),
      source_type: pageItem.source_type,
    });
    setFormErrors({});
    setShowModal(true);
    setMessagePopup(null);
  };

  // Input change
  const handleInputChange = (field: keyof PageFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) setFormErrors(prev => ({ ...prev, [field]: '' }));
  };

  // Validate
  const validateForm = () => {
    const errors: Record<string, string> = {};
    if (!formData.name.trim()) errors.name = 'Tên page là bắt buộc';
    if (!formData.page_id.trim()) {
      errors.page_id = 'Page ID là bắt buộc';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSubmitting(true);
    setMessage(null);

    try {
      const pagesEndpoint = getPagesEndpoints();
      const endpoint =
        modalMode === 'create'
          ? pagesEndpoint.CREATE
          : `${pagesEndpoint.UPDATE}/${editingPage?.id}`;
      const method = modalMode === 'create' ? 'POST' : 'PUT';

      const response = await fetchWithAuth(endpoint, {
        method,
        body: JSON.stringify({
          ...formData,
          page_id: formData.page_id || null,
        }),
      });

      if (response.success) {
        setMessagePopup({
          type: 'success',
          text: `Page ${
            modalMode === 'create' ? 'đã được tạo' : 'đã được cập nhật'
          } thành công!`,
        });
        setShowModal(false);
        loadPages(currentPage, searchTerm);
      } else {
        setMessagePopup({
          type: 'failure',
          text:
            getValidationMessages(response.data || {}) ||
            `Không thể ${modalMode === 'create' ? 'tạo' : 'cập nhật'} page`,
        });
      }
    } catch (error) {
      setMessagePopup({
        type: 'failure',
        text: `Đã xảy ra lỗi khi ${
          modalMode === 'create' ? 'tạo' : 'cập nhật'
        } page`,
      });
      console.error('Submit page error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // Delete
  const handleDelete = async (pageId: number) => {
    if (!window.confirm('Bạn có chắc chắn muốn xóa page này?')) return;

    try {
      const pagesEndpoint = getPagesEndpoints();
      const response = await fetchWithAuth(
        `${pagesEndpoint.DELETE}/${pageId}`,
        {
          method: 'DELETE',
        }
      );

      if (response.success) {
        setMessage({ type: 'success', text: 'Page đã được xóa thành công!' });
        loadPages(currentPage, searchTerm);
      } else {
        setMessage({ type: 'failure', text: 'Không thể xóa page' });
      }
    } catch (error) {
      setMessage({ type: 'failure', text: 'Đã xảy ra lỗi khi xóa page' });
      console.error('Delete page error:', error);
    }
  };

  // Grant access
  const responseFacebook = async (response: any, pageId: string) => {
    if (response.accessToken) {
      // Đây là **User Short-Lived Access Token**
      const userShortToken: string = response.accessToken;

      const responseGrantAccessPermission = await fetchWithAuth(
        '/facebook/grant-access-permission',
        {
          method: 'POST',
          body: JSON.stringify({ user_token: userShortToken, page_id: pageId }),
        }
      );

      if (responseGrantAccessPermission.success) {
        setMessage({
          type: 'success',
          text: 'Quyền truy cập đã được gắn thành công!',
        });
        loadPages(currentPage, searchTerm);
      } else {
        setMessage({
          type: 'failure',
          text: 'Đã xảy ra lỗi khi gắn quyền truy cập',
        });
        console.error(
          'Grant access permission error:',
          responseGrantAccessPermission.error
        );
      }
    }
  };

  const responseLongLiveToken = async (response: any, pageId: string) => {
    if (response.accessToken) {
      const longLiveToken = await fetchWithAuth(`/facebook/long-live-token`, {
        method: 'POST',
        body: JSON.stringify({
          user_token: response.accessToken,
          page_id: pageId,
        }),
      });
      if (longLiveToken.success) {
        setMessage({
          type: 'success',
          text: 'Long Live Token đã được lấy thành công!',
        });
      } else {
        setMessage({
          type: 'failure',
          text: 'Đã xảy ra lỗi khi lấy Long Live Token',
        });
        console.error('Long Live Token error:', longLiveToken.error);
      }
    }
  };

  const getPancakePageAccessToken = async (pageId: string) => {
    const response = await fetchWithAuth(
      `/facebook/pancake-page-access-token`,
      {
        method: 'POST',
        body: JSON.stringify({
          page_id: pageId,
        }),
      }
    );
    if (response.success) {
      setMessage({
        type: 'success',
        text: 'Pancake Page Access Token đã được lấy thành công!',
      });
    } else {
      setMessage({
        type: 'failure',
        text: 'Đã xảy ra lỗi khi lấy Pancake Page Access Token',
      });
    }
  };

  useEffect(() => {
    if (message) {
      setTimeout(() => {
        setMessage(null);
      }, 5000);
    }
  }, [message]);

  if (loading && pages.length === 0) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Spinner size="xl" />
          <p className="mt-4 text-gray-600">Đang tải danh sách page...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Pages</h1>
            <p className="text-gray-600">Quản lý danh sách pages</p>
          </div>
          <Button onClick={openCreateModal} className="btn-primary">
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            Tạo Page
          </Button>
        </div>
      </div>

      {message && (
        <Alert color={message.type} className="mb-4">
          <span>{message.text}</span>
        </Alert>
      )}

      {/* Search */}
      <Card className="mb-6">
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <TextInput
              placeholder="Tìm kiếm theo tên"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
            <Button onClick={handleSearch} className="btn-primary">
              Tìm kiếm
            </Button>
            <Button
              onClick={() => {
                setSearchTerm('');
                setCurrentPage(1);
                loadPages(1, '');
              }}
              className="btn-reset"
            >
              Xóa bộ lọc
            </Button>
          </div>
        </div>
      </Card>

      {/* Table */}
      <Card>
        <div className="overflow-x-auto">
          <Table>
            <Table.Head>
              <Table.HeadCell>Tên</Table.HeadCell>
              <Table.HeadCell>Page ID</Table.HeadCell>
              <Table.HeadCell>Trạng thái</Table.HeadCell>
              <Table.HeadCell>Ngày tạo</Table.HeadCell>
              <Table.HeadCell>Hành động</Table.HeadCell>
            </Table.Head>
            <Table.Body className="divide-y">
              {pages.map(pageItem => (
                <Table.Row
                  key={pageItem.id}
                  className="bg-white dark:border-gray-700 dark:bg-gray-800"
                >
                  <Table.Cell className="whitespace-nowrap font-medium text-gray-900 dark:text-white">
                    {pageItem.name}
                  </Table.Cell>
                  <Table.Cell className="text-gray-500 dark:text-gray-400">
                    <a
                      className="text-blue-500 hover:text-blue-700"
                      href={`${
                        pageItem.source_type === 'facebook'
                          ? `https://facebook.com/${pageItem.page_id}`
                          : 'javascript:void(0)'
                      }`}
                      target={
                        pageItem.source_type === 'facebook' ? '_blank' : ''
                      }
                      rel="noopener noreferrer"
                    >
                      {pageItem.page_id ?? '-'}
                    </a>
                  </Table.Cell>
                  <Table.Cell className="text-gray-500 dark:text-gray-400">
                    {pageItem.status === 'pending'
                      ? 'Chờ gắn quyền truy cập'
                      : 'Đã gắn quyền truy cập'}
                  </Table.Cell>
                  <Table.Cell className="text-gray-500 dark:text-gray-400">
                    {formatDate(pageItem.created_at)}
                  </Table.Cell>
                  <Table.Cell>
                    <div className="flex space-x-2">
                      {pageItem.source_type === 'facebook' ? (
                        <>
                          {/* @ts-ignore */}
                          <FacebookLogin
                            appId={process.env.REACT_APP_FACEBOOK_APP_ID ?? ''}
                            callback={(response: any) =>
                              responseFacebook(
                                response,
                                pageItem.page_id ?? '-'
                              )
                            }
                            render={(renderProps: any) => (
                              <Button
                                size="sm"
                                color="blue"
                                onClick={renderProps.onClick}
                              >
                                Gắn quyền truy cập
                              </Button>
                            )}
                          />
                          {/* @ts-ignore */}
                          <FacebookLogin
                            appId={process.env.REACT_APP_FACEBOOK_APP_ID ?? ''}
                            callback={(response: any) =>
                              responseLongLiveToken(
                                response,
                                pageItem.page_id ?? '-'
                              )
                            }
                            render={(renderProps: any) => (
                              <Button
                                size="sm"
                                color="blue"
                                onClick={renderProps.onClick}
                              >
                                Lấy Long Live Token
                              </Button>
                            )}
                          />
                          <Button
                            size="sm"
                            color="blue"
                            onClick={() =>
                              getPancakePageAccessToken(pageItem.page_id ?? '')
                            }
                          >
                            Lấy Pancake Page Access Token
                          </Button>
                        </>
                      ) : (
                        ''
                      )}
                      <Button
                        size="sm"
                        color="blue"
                        onClick={() => openEditModal(pageItem)}
                      >
                        Sửa
                      </Button>
                      <Button
                        size="sm"
                        color="red"
                        onClick={() => handleDelete(pageItem.id)}
                      >
                        Xóa
                      </Button>
                    </div>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-4">
            <div className="text-sm text-gray-700">
              Hiển thị {(currentPage - 1) * itemsPerPage + 1} đến{' '}
              {Math.min(currentPage * itemsPerPage, totalItems)} trong tổng số{' '}
              {totalItems} kết quả
            </div>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </Card>

      {/* Modal */}
      <Modal show={showModal} onClose={() => setShowModal(false)} size="2xl">
        <Modal.Header>
          {modalMode === 'create' ? 'Tạo Page Mới' : 'Chỉnh Sửa Page'}
        </Modal.Header>
        <form onSubmit={handleSubmit}>
          <Modal.Body>
            {messagePopup && (
              <Alert color={messagePopup.type} className="mb-4">
                <span>{messagePopup.text}</span>
              </Alert>
            )}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tên Page *
                </label>
                <TextInput
                  value={formData.name}
                  onChange={e => handleInputChange('name', e.target.value)}
                  placeholder="Nhập tên page"
                  color={formErrors.name ? 'failure' : undefined}
                  helperText={formErrors.name}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Page ID *
                </label>
                <TextInput
                  value={formData.page_id}
                  onChange={e => handleInputChange('page_id', e.target.value)}
                  placeholder="Nhập Page ID"
                  color={formErrors.page_id ? 'failure' : undefined}
                  helperText={formErrors.page_id}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Loại nguồn *
                </label>
                <Select
                  value={formData.source_type}
                  onChange={e =>
                    handleInputChange('source_type', e.target.value)
                  }
                >
                  <option value="facebook">Facebook</option>
                  <option value="tiktok">Tiktok</option>
                </Select>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              type="button"
              color="gray"
              onClick={() => setShowModal(false)}
            >
              Hủy
            </Button>
            <Button type="submit" disabled={submitting} className="btn-primary">
              {submitting ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Đang xử lý...
                </>
              ) : modalMode === 'create' ? (
                'Tạo Page'
              ) : (
                'Cập nhật'
              )}
            </Button>
          </Modal.Footer>
        </form>
      </Modal>
    </div>
  );
};

export default Pages;
